import { getTextFromHTML } from '@vuukle/utils/dist/es';
import { getMetaContentByName, isInCrossOriginFrame, isInIframe } from '~/utils/dom';
import type { IArticleConfig } from './types';

let articleUrl = '';
let articleImage = '';
let articleTitle = '';
let articleHost = '';
let articleLink = '';

const windowContext = isInIframe() && !isInCrossOriginFrame() && window.top ? window.top : window;

/**
 * Get page metadata
 * @param pageWindowContext
 */
function getPageMetadata(pageWindowContext: Window = window): {
  articleUrl: string;
  articleImage: string;
  articleTitle: string;
} {
  const canonicalLink = pageWindowContext.document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  const metaUrl = getMetaContentByName('property', 'og:url', pageWindowContext);
  const urlOfImage = getMetaContentByName('property', 'og:image', pageWindowContext);
  const titleOfArticle = getTextFromHTML(getMetaContentByName('property', 'og:title', pageWindowContext));
  let urlOfArticle = '';
  /** We should use the canonical link or the og:url metadata link only if the current page host is part of the canonical/og:url link */
  if (canonicalLink && canonicalLink.href.includes(pageWindowContext.location.host)) {
    urlOfArticle = canonicalLink.href;
  } else if (metaUrl && metaUrl.includes(pageWindowContext.location.host)) {
    urlOfArticle = metaUrl;
  } else {
    urlOfArticle = (pageWindowContext && pageWindowContext.location.href) || '';
  }

  return {
    articleUrl: urlOfArticle,
    articleImage: urlOfImage,
    articleTitle: titleOfArticle,
  };
}

try {
  const articleMetadata = getPageMetadata(windowContext);
  articleUrl = articleMetadata.articleUrl;
  articleImage = articleMetadata.articleImage;
  articleTitle = articleMetadata.articleTitle;
  articleHost = windowContext && windowContext.location.host.replace(/^www\./, '').replace(/^(m\.)/, '');
  articleLink = `${windowContext.location.protocol}//[url]`;
} catch (_e) {
  const articleMetadata = getPageMetadata();
  articleUrl = articleMetadata.articleUrl;
  articleImage = articleMetadata.articleImage;
  articleTitle = articleMetadata.articleTitle;
  articleHost = window && window.location.host.replace(/^www\./, '').replace(/^(m\.)/, '');
  articleLink = `${window.location.protocol}//[url]`;
}

const articleConfig: IArticleConfig = {
  apiKey: '',
  articleId: articleUrl,

  amp: false,
  sdk: false,
  customTranslationUrl: '',
  endlessMode: false,
  singlePage: false,
  globalRecommendations: false,
  host: articleHost,
  img: articleImage,
  language: document.documentElement.lang || 'en',
  link: articleLink,
  recommendationsWideImages: false,
  title: articleTitle,
  url: articleUrl,
  wordpressSync: false,

  ads: {
    enabled: true,
  },

  comments: {
    enabled: true,
    useIframe: true,
    hideCommentInputBoxWithButton: false,
    transliteration: {
      enabledByDefault: false,
      language: 'en',
    },
  },
  emotes: {
    disable: [],
    enabled: true,
  },
  powerbar: {
    enabled: true,
  },
  sharebar: {
    enabled: true,
  },
  subscribe: {
    enabled: true,
  },
  audio: {
    enabled: true,
  },
  quiz: {
    enabled: true,
  },
};

/**
 *
 */
function setRefHost() {
  // ❓ From where user came? Let's detect referrer
  try {
    if (windowContext.document.referrer) {
      // We need only host name from referrer so we can do this with
      // help of creating anchor and getting hostname from it.
      const refLink = windowContext.document.createElement('a');
      refLink.href = windowContext.document.referrer;
      articleConfig.refHost = refLink.hostname;
    }
  } catch (_e) {
    const refLink = document.createElement('a');
    refLink.href = document.referrer;
    articleConfig.refHost = refLink.hostname;
  }
}

// Run side-effects to update articleConfig.refHost
setRefHost();

/**
 * Create a function for testing purposes to reference the DOM in the context
 * it's called instead of being called immediately on import.
 */
export function getDefaultConfig(): IArticleConfig {
  const config = articleConfig;
  setRefHost();
  return config;
}

export default articleConfig;
